import { useState, useEffect } from "react";
import axios from "axios";
import { Wrench, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MaintenanceOverlay component that checks if maintenance mode is active.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render when not in maintenance
 * @param {string} props.region - Optional region to check (jamaica, usa, canada)
 * @param {string} props.assessmentToken - Optional assessment token to fetch candidate's region
 */
export default function MaintenanceOverlay({ children, region = null, assessmentToken = null }) {
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [candidateRegion, setCandidateRegion] = useState(region);

  // Fetch candidate's region from assessment token if provided
  useEffect(() => {
    const fetchCandidateRegion = async () => {
      if (assessmentToken && !region) {
        try {
          // Try the simple region endpoint first (for regular assessment tokens)
          let response = await axios.get(`${API}/assessment/${assessmentToken}/region`).catch(() => null);
          
          if (response?.data?.region) {
            setCandidateRegion(response.data.region);
            return;
          }
          
          // Fallback to dynamic assessments endpoint
          response = await axios.get(`${API}/dynamic-assessments/take/${assessmentToken}`);
          const fetchedRegion = response.data?.candidate?.region;
          if (fetchedRegion) {
            setCandidateRegion(fetchedRegion);
          }
        } catch (err) {
          // Silently fail - will use global maintenance check
          console.log("Could not fetch candidate region, using global check");
        }
      }
    };
    fetchCandidateRegion();
  }, [assessmentToken, region]);

  const checkMaintenanceStatus = async () => {
    try {
      // Determine which region to check
      const regionToCheck = candidateRegion || region;
      
      // If region is provided, check specific region maintenance
      const url = regionToCheck 
        ? `${API}/maintenance-status?region=${regionToCheck.toLowerCase()}`
        : `${API}/maintenance-status`;
      
      const response = await axios.get(url);
      setMaintenanceActive(response.data.enabled);
      setMaintenanceMessage(response.data.message || "System update in progress. Please check back shortly.");
    } catch (err) {
      // If endpoint fails, assume maintenance is not active
      console.error("Error checking maintenance status:", err);
      setMaintenanceActive(false);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  useEffect(() => {
    checkMaintenanceStatus();
    
    // Check every 30 seconds in case maintenance mode changes
    const interval = setInterval(checkMaintenanceStatus, 30000);
    return () => clearInterval(interval);
  }, [candidateRegion, region]);

  const handleRefresh = () => {
    setChecking(true);
    checkMaintenanceStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#00AEEF] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-[#6b7280] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (maintenanceActive) {
    return (
      <div className="min-h-screen bg-black/95 flex items-center justify-center p-4">
        {/* Blurred background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900"></div>
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-30">
            <div className="absolute top-10 right-10 w-80 h-60 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-3xl blur-sm transform rotate-6"></div>
            <div className="absolute bottom-20 right-20 w-64 h-48 bg-gradient-to-br from-gray-700/30 to-gray-800/30 rounded-2xl blur-sm transform -rotate-3"></div>
          </div>
        </div>
        
        {/* Main modal - split pane design */}
        <div className="relative max-w-4xl w-full flex rounded-2xl overflow-hidden shadow-2xl">
          {/* Left side - Image/Visual */}
          <div className="hidden md:block w-1/2 relative bg-gradient-to-br from-cyan-400 via-teal-500 to-emerald-500">
            {/* Decorative elements */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Animated gear/wrench icon */}
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center transform rotate-12 shadow-xl">
                  <Wrench className="w-16 h-16 text-white" />
                </div>
                {/* Floating elements */}
                <div className="absolute -top-8 -right-8 w-16 h-16 bg-yellow-400 rounded-2xl flex items-center justify-center shadow-lg animate-bounce" style={{ animationDuration: '3s' }}>
                  <Clock className="w-8 h-8 text-yellow-900" />
                </div>
                <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl"></div>
              </div>
            </div>
            {/* Bottom gradient overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
          
          {/* Right side - Content */}
          <div className="w-full md:w-1/2 bg-white p-8 md:p-10 relative">
            {/* Close/X button placeholder (non-functional, just for design) */}
            <button className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Status badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
              Maintenance Mode
            </div>
            
            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              System Update
              <br />
              <span className="text-gray-600">in Progress</span>
            </h1>
            
            {/* Price-like styling for message */}
            <p className="text-gray-500 text-base mb-6">
              {maintenanceMessage}
            </p>
            
            {/* Feature list */}
            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">Your assessment progress has been saved automatically</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">No data will be lost during the maintenance period</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">We'll be back shortly - thank you for your patience</span>
              </div>
            </div>
            
            {/* CTA Button */}
            <Button
              onClick={handleRefresh}
              disabled={checking}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 text-base font-medium rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200"
            >
              {checking ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Checking Status...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Check Again
                </>
              )}
            </Button>
            
            {/* Footer link */}
            <p className="text-center text-gray-400 text-sm mt-6">
              Need help? Contact{' '}
              <a href="mailto:supportsagicor@thcohq.com" className="text-blue-600 hover:text-blue-700 hover:underline">
                supportsagicor@thcohq.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
